import isEqual from "lodash.isequal";
import { create } from "zustand";

import { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import { filterSections } from "@/components/molecules/filter/FilterSection";

type SearchState = {
  filter: SearchParams;
  submitted: boolean;
  setFilter: (filter: SearchParams) => void;
  submittedFilter: SearchParams;
  submitFilter: (filter: SearchParams) => void;
  resetFilter: () => void;
  filterDropdown: string[];
  setFilterDropdown: (filterDropdown: string[]) => void;

  detailId: string | null;
  closeDetail: () => void;
  openDetail: (id: string) => void;

  changedCompanyIds: string[];
  addChangedCompanyIds: (ids: string | string[]) => void;
};

export const defaultFilter = {
  companyInclude: [],
  companyExclude: [],
  showTerminated: false,
  locationInclude: [],
  locationExclude: [],
  headcount: [],
  headcountFree: { min: "", max: "" },
  hq: false,
  industryInclude: [],
  advancedIndustryInclude: [],
  advancedIndustryIncludeMainIds: [],
  industryExclude: [],
  advancedIndustryExclude: [],
  advancedIndustryExcludeMainIds: [],
  revenue: [],
  revenueFree: { min: "", max: "" },
  revenueGrowth: [],
  revenueGrowthFree: { min: "", max: "" },
  foundingDate: { min: "", max: "" },
  predefinedFoundingDate: [],
  keywords: [],
  keywordsExclude: [],
};

const useSearchStore = create<SearchState>()((set) => ({
  filter: defaultFilter,
  submitted: false,
  setFilter: (filter) => set({ filter }),
  submittedFilter: defaultFilter,
  submitFilter: (filter) =>
    set((state) => {
      const hasChanged = !isEqual(filter, state.submittedFilter);

      return {
        submittedFilter: filter,
        submitted: true,
        detailId: null,
        changedCompanyIds: hasChanged ? [] : state.changedCompanyIds,
      };
    }),
  resetFilter: () =>
    set({ filter: defaultFilter, submittedFilter: defaultFilter, submitted: false, changedCompanyIds: [] }),

  filterDropdown: [filterSections.company],
  setFilterDropdown: (filterDropdown) => set({ filterDropdown }),

  detailId: null,
  closeDetail: () => set({ detailId: null }),
  openDetail: (id) => set({ detailId: id }),

  changedCompanyIds: [],
  addChangedCompanyIds: (ids) =>
    set((state) => {
      const incoming = typeof ids === "string" ? [ids] : ids;
      // Build a new Set of existing + incoming, auto-deduped
      const merged = new Set<string>(state.changedCompanyIds);
      incoming.forEach((id) => merged.add(id));
      return { changedCompanyIds: Array.from(merged) };
    }),
}));

export default useSearchStore;
